import { useState, useMemo } from "react";
import Styles from "./DataTable.module.css";
import { Column_sorting, Column_filter } from "../../controller/Images";
import DateRangePicker from "../datepicker/DateRangePicker";
import { View_icon, View_not_icon } from "../../controller/Images";
import Buttons from "../buttons/Buttons";

const DataTable = ({
  columns,
  data,
  filterOptions = {},
  filters = {},
  actionIcon,
  ellipsisIndex,
  onSort,
  onDateRangeApply,
  handleApplyFilter,
  handleFilter,
  isOpens,
  filterPopupRef,
  tempCategoricalFilters,
  onCategoricalFilterChange,
  currentDateRange,
  onClearSpecificFilter,
  loading = false,
}) => {
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });
  const [hoveredRowIndex, setHoveredRowIndex] = useState(null);

  const sortedData = useMemo(() => {
    if (!sortConfig.key) return data;

    const sorted = [...data].sort((a, b) => {
      const aVal = a[sortConfig.key];
      const bVal = b[sortConfig.key];

      if (aVal < bVal) return sortConfig.direction === "asc" ? -1 : 1;
      if (aVal > bVal) return sortConfig.direction === "asc" ? 1 : -1;
      return 0;
    });

    return sorted;
  }, [data, sortConfig]);

  const isFilterActive = (colAccessor, isDateColumn) => {
    if (isDateColumn) {
      return filters[`${colAccessor}_from`] || filters[`${colAccessor}_to`];
    }
    return filters[colAccessor];
  };

  return (
    <>
      <div
        className={`table-responsive position-relative mb-3 ${Styles.height}`}
      >
        <table className={`table striped align-middle ${Styles.tableShadow}`}>
          <thead className={Styles.tableHead}>
            <tr>
              {columns.map((col, colIndex) => (
                <th
                  key={col.accessor}
                  className={`${Styles.noWrapHeader} ${
                    colIndex !== 0 ? Styles.tableCell : ""
                  }`}
                >
                  <div className="d-flex align-items-center position-relative">
                    <span
                      className="text-start"
                      style={{
                        fontSize: "16px",
                        fontWeight: 600,
                        cursor: col.sortable ? "pointer" : "default",
                      }}
                    >
                      {col.header}
                    </span>

                    {col.sortable && (
                      <img
                        src={Column_sorting}
                        alt="sort"
                        width="16"
                        height="16"
                        style={{ marginLeft: "4px", cursor: "pointer" }}
                        onClick={() => {
                          let direction = "asc";
                          if (
                            sortConfig.key === col.accessor &&
                            sortConfig.direction === "asc"
                          ) {
                            direction = "desc";
                          }
                          setSortConfig({ key: col.accessor, direction });
                          onSort?.(col.accessor, direction);
                        }}
                      />
                    )}

                    {col.filterable && (
                      <img
                        src={Column_filter}
                        alt="filter"
                        width="24"
                        height="24"
                        style={{
                          marginLeft: "6px",
                          cursor: "pointer",
                          backgroundColor: isFilterActive(
                            col.accessor,
                            col.isDateColumn
                          )
                            ? "#fa8316"
                            : "transparent",
                          borderRadius: "4px",
                        }}
                        onClick={() => handleFilter(col.accessor)}
                      />
                    )}

                    {isOpens?.status && isOpens?.column === col.accessor && (
                      <div
                        ref={filterPopupRef}
                        className="position-absolute bg-white shadow p-2 border custom-radio"
                        style={{
                          top: "150%",
                          right: "10%",
                          zIndex: 100,
                          minWidth: "300px",
                          color: "#142454",
                          fontSize: "16px",
                        }}
                      >
                        <form>
                          {col.isDateColumn ? (
                            <DateRangePicker
                              initialFromDate={currentDateRange?.fromDate}
                              initialToDate={currentDateRange?.toDate}
                              onApply={onDateRangeApply}
                              myData={currentDateRange}
                            />
                          ) : (
                            filterOptions[col.accessor]?.map(
                              ({ label, value }) => (
                                <div className="form-check mb-2" key={value}>
                                  <input
                                    className="form-check-input"
                                    type="radio"
                                    name={`filterRadio-${col.accessor}`}
                                    id={`${value}-${col.accessor}`}
                                    value={value}
                                    checked={
                                      tempCategoricalFilters[col.accessor] ===
                                      value
                                    }
                                    onChange={(e) =>
                                      onCategoricalFilterChange(
                                        col.accessor,
                                        e.target.value
                                      )
                                    }
                                  />
                                  <label
                                    className="form-check-label"
                                    htmlFor={`${value}-${col.accessor}`}
                                  >
                                    {label}
                                  </label>
                                </div>
                              )
                            )
                          )}
                        </form>

                        {!col.isDateColumn && (
                          <div className="d-flex justify-content-end gap-2 mt-2">
                            <Buttons
                              type="button"
                              className=""
                              text="Clear"
                              variant="secondary"
                              onClick={() => {
                                onClearSpecificFilter(col.accessor);
                              }}
                            />
                            <Buttons
                              type="button"
                              className=""
                              text="Apply"
                              onClick={handleApplyFilter}
                            ></Buttons>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </th>
              ))}

              {actionIcon && (
                <th
                  className={`${Styles.sticky_action_column} ${Styles.tableCell}`}
                >
                  Action
                </th>
              )}
            </tr>
          </thead>

          <tbody className={Styles.tableBody}>
            {sortedData.length > 0 ? (
              sortedData.map((row, index) => (
                <tr key={index}>
                  {columns.map((col, colIndex) => (
                    <td
                      key={col.accessor}
                      className={`${
                        ellipsisIndex?.includes(colIndex) ? "ellipsisText" : ""
                      } ${colIndex !== 0 ? Styles.tableCell : ""}`}
                      style={{
                        fontSize: "16px",
                        fontWeight: 400,
                        color: "#142454",
                        cursor: "pointer",
                      }}
                      title={
                        ellipsisIndex?.includes(colIndex)
                          ? row[col.accessor]
                          : undefined
                      }
                    >
                      {row[col.accessor]}
                    </td>
                  ))}
                  {actionIcon && (
                    <td
                      className={`${Styles.sticky_action_column} ${Styles.tableCell}`}
                    >
                      <img
                        src={
                          hoveredRowIndex === index ? View_icon : View_not_icon
                        }
                        alt="action"
                        style={{ cursor: "pointer" }}
                        onMouseEnter={() => setHoveredRowIndex(index)}
                        onMouseLeave={() => setHoveredRowIndex(null)}
                        onClick={() => actionIcon?.onClick?.(row)}
                      />
                    </td>
                  )}
                </tr>
              ))
            ) : !loading ? (
              <tr>
                <td
                  colSpan={columns.length + (actionIcon ? 1 : 0)}
                  className="text-muted text-center"
                >
                  No data found.
                </td>
              </tr>
            ) : null}
          </tbody>
        </table>
      </div>
    </>
  );
};

export default DataTable;